import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormField, formInputClass } from './index';
import { fetchCountries, fetchSubdivisions, findCountryByName } from '../../services/geoService';

/**
 * Selectores de país y provincia/estado (API /api/geo/*).
 * @param {string} countryName - Nombre del país (ej. Argentina), compatible con campo `pais`.
 * @param {string} provinceName - Nombre de provincia/estado.
 */
export default function CountrySubdivisionSelect({
  countryName = '',
  provinceName = '',
  onCountryChange,
  onProvinceChange,
  signupOnly = false,
  lockCountry = false,
  defaultCountryCode = 'AR',
  showProvince = true,
  cityLabel = 'Ciudad / localidad',
  cityValue = '',
  onCityChange,
  showCity = false,
  countryRequired = true,
  provinceRequired = false,
  countryError = '',
  provinceError = '',
  tc,
  compact = false,
  className = '',
}) {
  const inputCls = formInputClass(tc?.inputClass, { compact });
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState('');
  const [subdivisions, setSubdivisions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingSubdivisions, setLoadingSubdivisions] = useState(false);
  const [geoError, setGeoError] = useState('');
  const appliedDefaultCountry = useRef(false);
  const onCountryChangeRef = useRef(onCountryChange);
  onCountryChangeRef.current = onCountryChange;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCountries(true);
      setGeoError('');
      try {
        const list = await fetchCountries({ signupOnly });
        if (cancelled) return;
        setCountries(list);
      } catch (err) {
        if (!cancelled) setGeoError(err.message || 'Error al cargar países');
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signupOnly]);

  useEffect(() => {
    if (!countries.length) return;
    const fromProp = findCountryByName(countries, countryName);
    const initial = fromProp?.code
      || (signupOnly || lockCountry ? defaultCountryCode : '');
    if (
      initial
      && !countryName
      && !appliedDefaultCountry.current
    ) {
      const row = countries.find((c) => c.code === initial);
      if (row) {
        appliedDefaultCountry.current = true;
        onCountryChangeRef.current?.(row.name, row.code);
      }
    }
    const nextCode = fromProp?.code || initial || '';
    setCountryCode((prev) => (prev === nextCode ? prev : nextCode));
  }, [countries, countryName, signupOnly, lockCountry, defaultCountryCode]);

  useEffect(() => {
    if (!countryCode) {
      setSubdivisions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingSubdivisions(true);
      try {
        const data = await fetchSubdivisions(countryCode);
        if (cancelled) return;
        setSubdivisions(Array.isArray(data.subdivisions) ? data.subdivisions : []);
      } catch {
        if (!cancelled) setSubdivisions([]);
      } finally {
        if (!cancelled) setLoadingSubdivisions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const provinceSelectValue = useMemo(() => {
    if (!provinceName) return '';
    const match = subdivisions.find(
      (s) => s.name.toLowerCase() === String(provinceName).trim().toLowerCase()
    );
    return match ? match.name : provinceName;
  }, [provinceName, subdivisions]);

  const handleCountrySelect = (e) => {
    const code = e.target.value;
    setCountryCode(code);
    const row = countries.find((c) => c.code === code);
    onCountryChange?.(row?.name || '', code);
    onProvinceChange?.('', '');
  };

  const handleProvinceSelect = (e) => {
    onProvinceChange?.(e.target.value, '');
  };

  const hasSubdivisions = subdivisions.length > 0;
  const countryDisabled = lockCountry || signupOnly || loadingCountries;

  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 ${
        compact ? 'gap-x-2 gap-y-1.5' : 'gap-x-3 gap-y-2.5'
      } ${className}`.trim()}
    >
      {geoError && (
        <p className="sm:col-span-2 text-xs text-amber-600 dark:text-amber-400">{geoError}</p>
      )}
      <FormField
        label="País"
        required={countryRequired}
        error={countryError}
        labelClass={tc?.labelClass}
      >
        <select
          className={inputCls}
          value={countryCode}
          onChange={handleCountrySelect}
          disabled={countryDisabled}
          required={countryRequired}
        >
          <option value="">
            {loadingCountries ? 'Cargando países…' : 'Seleccioná un país'}
          </option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </FormField>

      {showProvince && (
        <FormField
          label="Provincia / estado"
          required={provinceRequired && hasSubdivisions}
          error={provinceError}
          labelClass={tc?.labelClass}
        >
          {hasSubdivisions ? (
            <select
              className={inputCls}
              value={provinceSelectValue}
              onChange={handleProvinceSelect}
              disabled={!countryCode || loadingSubdivisions}
              required={provinceRequired}
            >
              <option value="">
                {loadingSubdivisions ? 'Cargando…' : 'Seleccioná provincia o estado'}
              </option>
              {subdivisions.map((s) => (
                <option key={`${countryCode}-${s.code}`} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={inputCls}
              value={provinceName}
              onChange={(e) => onProvinceChange?.(e.target.value, '')}
              placeholder={countryCode ? 'Provincia o estado (texto libre)' : 'Elegí un país primero'}
              disabled={!countryCode}
            />
          )}
        </FormField>
      )}

      {showCity && (
        <FormField label={cityLabel} className="sm:col-span-2" labelClass={tc?.labelClass}>
          <input
            className={inputCls}
            value={cityValue}
            onChange={(e) => onCityChange?.(e.target.value)}
            placeholder="Ciudad o localidad"
          />
        </FormField>
      )}
    </div>
  );
}
